
import CryptoJS from 'crypto-js';

// Browser-safe encrypted cache using localStorage
const CACHE_PREFIX = 'ctrader_encrypted_';
const ENCRYPTION_KEY = 'ctrader_cache_key_v1'; // In production, this should be more secure

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class EncryptedOAuthCache {
  private encrypt(data: any): string {
    try {
      const jsonString = JSON.stringify(data);
      return CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString();
    } catch (error) {
      console.warn('Encryption failed, storing unencrypted:', error);
      return JSON.stringify(data);
    }
  }

  private decrypt(encryptedData: string): any {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
      const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedData);
    } catch (error) {
      console.warn('Decryption failed, trying as plain JSON:', error);
      try {
        return JSON.parse(encryptedData);
      } catch (parseError) {
        console.warn('Failed to parse cached data:', parseError);
        return null;
      }
    }
  }

  private isExpired(item: CacheItem<any>): boolean {
    return Date.now() > item.expiresAt;
  }

  private setItem<T>(key: string, data: T, ttlMs: number = 5 * 60 * 1000): void {
    try {
      const cacheItem: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + ttlMs
      };
      
      const encrypted = this.encrypt(cacheItem);
      localStorage.setItem(`${CACHE_PREFIX}${key}`, encrypted);
    } catch (error) {
      console.warn('Failed to cache item:', error);
    }
  }

  private getItem<T>(key: string): T | null {
    try {
      const encrypted = localStorage.getItem(`${CACHE_PREFIX}${key}`);
      if (!encrypted) return null;

      const cacheItem = this.decrypt(encrypted) as CacheItem<T>;
      if (!cacheItem || this.isExpired(cacheItem)) {
        localStorage.removeItem(`${CACHE_PREFIX}${key}`);
        return null;
      }

      return cacheItem.data;
    } catch (error) {
      console.warn('Failed to retrieve cached item:', error);
      return null;
    }
  }

  // Trading accounts cache
  setTradingAccounts(accounts: any[], cspAccountId: string): void {
    this.setItem(`trading_accounts_${cspAccountId}`, accounts, 5 * 60 * 1000); // 5 minutes
  }

  getTradingAccounts(cspAccountId: string): any[] | null {
    return this.getItem<any[]>(`trading_accounts_${cspAccountId}`);
  }

  clearTradingAccounts(cspAccountId: string): void {
    try {
      localStorage.removeItem(`${CACHE_PREFIX}trading_accounts_${cspAccountId}`);
    } catch (error) {
      console.warn('Failed to clear trading accounts cache:', error);
    }
  }

  // Admin credentials cache
  setAdminCredentials(credentials: any): void {
    this.setItem('admin_credentials', credentials, 30 * 60 * 1000); // 30 minutes
  }

  getAdminCredentials(): any | null {
    return this.getItem<any>('admin_credentials');
  }

  // Clear all cache
  clearAll(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear all cache:', error);
    }
  }

  // Maintenance - clean expired entries
  performMaintenance(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          const encrypted = localStorage.getItem(key);
          if (encrypted) {
            const cacheItem = this.decrypt(encrypted);
            if (cacheItem && this.isExpired(cacheItem)) {
              keysToRemove.push(key);
            }
          }
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to perform cache maintenance:', error);
    }
  }
}

export const encryptedOAuthCache = new EncryptedOAuthCache();
